import { roundHalfUp, type Cents } from './money.js';

/** NJ sales tax on an amount. Rate in parts-per-million (6.625% = 66250). Half-up to the cent. */
export function salesTax(amountCents: Cents, ratePpm: number): Cents {
  return roundHalfUp((amountCents * ratePpm) / 1_000_000);
}

/**
 * Allocate tax across lines so the lines sum exactly to the tax on the total (largest-remainder).
 * Returns per-line tax in input order.
 */
export function allocateTax(lineAmounts: Cents[], ratePpm: number): Cents[] {
  const total = salesTax(lineAmounts.reduce((a, b) => a + b, 0), ratePpm);
  const raw = lineAmounts.map((a) => (a * ratePpm) / 1_000_000);
  const floors = raw.map((r) => Math.floor(r));
  let left = total - floors.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac);
  for (const o of order) {
    if (left <= 0) break;
    floors[o.i]! += 1;
    left -= 1;
  }
  return floors;
}

/**
 * Cash-only rounding to the nearest 5 cents (penny phase-out). Returns the signed adjustment (-2..+2).
 * Per NJ ABC AN 2026-10 the 1–4 cent adjustment is made after tax, does not change price or cost,
 * must be disclosed, and the books must record it exactly.
 */
export function cashRoundingAdjustment(totalCents: Cents): Cents {
  const r = ((totalCents % 5) + 5) % 5;
  if (r === 0) return 0;
  return r <= 2 ? -r : 5 - r;
}
